import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Link,
  Switch,
  Typography,
} from "@mui/material";

import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { ConfirmModal } from "components";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ContentStructureField from "./ContentStructureField";
import SourceOfKnowledgeField from "./SourceOfKnowledgeField";
import WhattoExpectField from "./WhattoExpectField";
import SummaryField from "./SummaryField";
import PricingField from "./PricingField";
import Promotions from "./Promotions";
import ContentThumbnail from "./ContentThumbnail";
import SelectableBoxCommon from "./SelectableBoxCommon";
import CategoryField from "./CategoryField";
import ContentTitleField from "./ContentTitleField";
import { ContentsService } from "services/ContentsService";
import { useAuth } from "context/authContext";
import { Content as ContentType } from "types";
import { DocumentReference, Timestamp } from "firebase/firestore";
import { set } from "react-hook-form";
import AgreeToTerms from "./AgreeToTerms";

type SelectedValueType =
  | "contentstructure"
  | "sourceofknowledge"
  | "category"
  | "contentTitle"
  | "summary"
  | "whattoexpect"
  | "pricing"
  | "promotions"
  | "contentthumbnail"
  | "agreeToTerms";

const CreateHandoutData = () => {
  const { currentUser } = useAuth();
  const searchParams = useSearchParams();
  const handoutId = searchParams[0].get("id");

  const [loading, setLoading] = useState(false);
  const [selectedValue, setSelectedValue] =
    useState<SelectedValueType>("contentstructure");
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const handleOpenModal = () => setConfirmModalOpen(true);
  const handleCloseModal = () => setConfirmModalOpen(false);
  const [autoPay, setAutoPay] = useState(false);
  const [showIcons, setShowIcons] = useState({
    contentstructure: false,
    sourceofknowledge: false,
    category: false,
    contentTitle: false,
    summary: false,
    whattoexpect: false,
    pricing: false,
    promotions: false,
    contentthumbnail: false,
    agreeToTerms: false,
  });
  const [mode, setMode] = useState<"add" | "edit">("add");
  const [isAgreed, setIsAgreed] = useState(false);

  // Single state to store all the form data
  const [contentData, setContentData] = useState<ContentType>({
    id: "",
    createdBy: null,
    type: "handout",
    category: "Psychotherapy",
    title: "",
    coverPhotoUrl: "",
    isPublished: false,
    summary: "",
    expectations: "",
    sourceOfKnowledge: "Clinical Insights",
    price: 0,
    promotion: {
      isActive: false,
      discountPercent: 0,
      isFree: false,
      code: "",
    },
    purchaseCount: 0,
    revenue: 0,
    currentStatus: {
      status: "draft",
      date: Timestamp.now(),
    },
    statusHistory: [
      {
        status: "draft",
        date: Timestamp.now(),
      },
    ],
    contentStructure: [],
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });

  // Fetch the handout data if editing
  useEffect(() => {
    const fetchHandoutData = async (id: string) => {
      setLoading(true);
      try {
        // Fetch all handouts
        const handouts = await ContentsService.getContents();

        // Filter the handout with the matching ID
        const matchingHandout = handouts.find((handout) => handout.id === id);

        if (matchingHandout) {
          setContentData(matchingHandout);
          setMode("edit");
          updateShowIcons(matchingHandout);
        } else {
          console.log("No handout found with the provided ID.");
        }
      } catch (error) {
        console.error("Error fetching handout data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (handoutId) fetchHandoutData(handoutId);
  }, [handoutId]);

  const updateShowIcons = (data: ContentType) => {
    setShowIcons({
      contentstructure: data.contentStructure.length > 0,
      sourceofknowledge: !!data.sourceOfKnowledge,
      category: !!data.category,
      contentTitle: !!data.title,
      summary: !!data.summary,
      whattoexpect: !!data.expectations,
      pricing: data.price > 0,
      promotions: data.promotion.isActive || data.price > 0,
      contentthumbnail: !!data.coverPhotoUrl,
      agreeToTerms: isAgreed, // or any condition you have
    });
  };

  const handleNavigateHandoutBack = () => {
    navigate(-1);
  };

  const handleConfirm = async () => {
    try {
      const id = currentUser?.uid;

      if (!id) {
        console.error("User is not authenticated");
        return;
      }

      if (mode === "edit" && contentData.id) {
        // Update existing handout
        await handleUpdateHandout(contentData.id, contentData);
        console.log("Handout updated successfully");
      } else if (mode === "add") {
        // Create a new handout
        await ContentsService.createContents(id, [contentData]);
        console.log("Handout created successfully");
      }

      // Navigate after update or create
      navigate("/my-content");
    } catch (error) {
      console.error("Failed to save handout:", error);
    } finally {
      setConfirmModalOpen(false);
    }
  };

  const handleUpdateHandout = async (
    id: string,
    updatedData: Partial<ContentType>
  ) => {
    try {
      await ContentsService.updateContent(id, updatedData);

      // Update the state to reflect changes in the UI
      setContentData((prevData) => ({
        ...prevData,
        ...updatedData,
      }));

      console.log("Handout updated successfully!");
    } catch (error) {
      console.error("Failed to update handout:", error);
    }
  };

  const allSteps: SelectedValueType[] = [
    "contentstructure",
    "sourceofknowledge",
    "category",
    "contentTitle",
    "summary",
    "whattoexpect",
    "pricing",
    "promotions",
    "contentthumbnail",
    "agreeToTerms",
  ];

  // Condition to skip the first two steps
  const skipFirstTwoSteps = true;
  const steps = skipFirstTwoSteps ? allSteps.slice(0) : allSteps;

  const headers: Record<SelectedValueType, string> = {
    contentstructure: "Content Structure",
    sourceofknowledge: "Source of Knowledge",
    category: "Category",
    contentTitle: "Content Title",
    summary: "Summary",
    whattoexpect: "What to Expect",
    pricing: "Pricing",
    promotions: "Promotions",
    contentthumbnail: "Content Thumbnail",
    agreeToTerms: "Agree to Terms",
  };

  const handleSelectionChange = (value: SelectedValueType) => {
    const stepIndex = allSteps.indexOf(value);
    // if (stepIndex !== -1) {
    if (stepIndex !== -1 && stepIndex === currentStep) {

      setCurrentStep(stepIndex);
      setSelectedValue(value);
    }

    setIsAgreed(false);
    setShowIcons((prevState: any) => ({
      ...prevState,
      agreeToTerms: false,
    }));
  };

  const handleNextClick = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setSelectedValue(steps[currentStep + 1]);
    }
    setIsAgreed(false);

    // If user has entered a price and is moving past "Pricing", auto-mark "Promotions" as complete
    if (selectedValue === "pricing" && contentData.price > 0) {
      setShowIcons((prevState: any) => ({
        ...prevState,
        promotions: true, // Auto-mark Promotions as complete
      }));
    }

  };

  const handleAgreeClick = () => {
    setIsAgreed(true);
    setShowIcons((prevState: any) => ({
      ...prevState,
      agreeToTerms: true,
    }));
  };

  if (loading) return <Box>Loading</Box>;

  return (
    <>
      <Grid
        display="flex"
        flexDirection="column"
        pb={10}
        justifyContent="center"
        gap={{ xs: 2, md: 5 }}
        px={{ xs: 2, md: 5 }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          flexDirection={{ xs: "column", md: "row" }}
          gap={{ xs: 2, md: 5 }}
        >
          <Box
            onClick={handleNavigateHandoutBack}
            pt={{ xs: 15, md: 5 }}
            fontWeight={500}
            fontSize="28px"
            color="#233F91"
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ cursor: "pointer" }}
          >
            <KeyboardArrowLeftIcon
              sx={{ color: "#233F91", fontSize: "28px" }}
            />
            {mode === "edit" ? "Update a Handout" : "Create a Handout"}
          </Box>

          {/* <Grid
            flexDirection="column"
            pt={{ xs: 0, md: 8 }}
            gap={2}
            alignItems="center"
          >
            <Box display="flex" mb={1} alignItems="center" gap={2}>
              <Typography
                variant="body1"
                color="#000000"
                fontSize={18}
                fontWeight={700}
              >
                Publication Date:
              </Typography>
              <Typography
                variant="body2"
                color="#797979"
                fontSize={18}
                fontWeight={400}
              >
                DD / MM / YYYY
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <Typography
                variant="body1"
                color="#000000"
                fontSize={18}
                fontWeight={700}
              >
                Status:
              </Typography>
              <Box>
                <FormControlLabel
                  label="Published"
                  labelPlacement="start"
                  control={
                    <Switch
                      checked={autoPay}
                      onChange={(e) => setAutoPay(e.target.checked)}
                      color="primary"
                    />
                  }
                />
              </Box>
            </Box>
          </Grid> */}
        </Box>

        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          minHeight="100vh"
          mt={3}
        >
          <Box width={{ xs: "100%", md: "22%" }} p={2}>
            <Typography
              variant="h6"
              gutterBottom
              fontSize={18}
              fontWeight={700}
              color="#595959"
            >
              Plan Your Content
            </Typography>

            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="contentstructure"
              onClick={handleSelectionChange}
              text="Content Structure"
              showIcon={showIcons.contentstructure}
            />

            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="sourceofknowledge"
              onClick={handleSelectionChange}
              text="Source of Knowledge"
              showIcon={showIcons.sourceofknowledge}
            />

            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="category"
              onClick={handleSelectionChange}
              text="Category"
              showIcon={showIcons.category}
            />

            <Typography
              variant="h6"
              gutterBottom
              mt={2}
              fontSize={18}
              fontWeight={700}
              color="#595959"
            >
              Create Your Content
            </Typography>

            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="contentTitle"
              onClick={handleSelectionChange}
              text="Content Title"
              showIcon={showIcons.contentTitle}
            />

            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="summary"
              onClick={handleSelectionChange}
              text="Summary"
              showIcon={showIcons.summary}
            />

            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="whattoexpect"
              onClick={handleSelectionChange}
              text="What to Expect"
              showIcon={showIcons.whattoexpect}
            />

            <Typography
              variant="h6"
              gutterBottom
              mt={2}
              fontSize={18}
              fontWeight={700}
              color="#595959"
            >
              Publish Your Content
            </Typography>

            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="pricing"
              onClick={handleSelectionChange}
              text="Pricing"
              showIcon={showIcons.pricing}
            />

            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="promotions"
              onClick={handleSelectionChange}
              text="Promotions"
              showIcon={showIcons.promotions}
            />

            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="contentthumbnail"
              onClick={handleSelectionChange}
              text="Content Thumbnail"
              showIcon={showIcons.contentthumbnail}
            />

            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="agreeToTerms"
              onClick={handleSelectionChange}
              text="Agree to Terms"
              showIcon={showIcons.agreeToTerms}
            />

            <Box
              display={"flex"}
              justifyContent={"flex-end"}
              mt={2}
              flexDirection={"column"}
              height={"20%"}
            >
              <Button
                sx={{
                  width: "214px",
                  height: "48px",
                  p: "6px 20px",
                  backgroundColor: isAgreed ? "#233F91" : "#595959",
                  borderRadius: "15px",
                  color: isAgreed ? "white" : "white",
                  "&:hover": {
                    backgroundColor: isAgreed ? "#233F91" : "#595959",
                    color: isAgreed ? "white" : "#233F91",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#595959", // Background when disabled
                    color: "white", // Text color when disabled
                  },
                }}
                type="submit"
                disabled={!isAgreed}
                onClick={handleOpenModal}
              >
                Publish
              </Button>

              <ConfirmModal
                title="You're About to Submit Your Changes"
                subtitle="Please ensure all details are correct before submitting. Once confirmed, your submission will be processed and changes cannot be reversed."
                cancelText="Cancel"
                confirmText="Confirm"
                isLoading={false}
                onCancel={handleCloseModal}
                onConfirm={handleConfirm}
                isConfirmModal={isConfirmModalOpen}
                icon={
                  <ErrorOutlineIcon
                  // sx={{ color: COLORS.text.error, fontSize: 40 }}
                  />
                }
              />
            </Box>
          </Box>
          <Box
            width={{ xs: "100%", md: "80%" }}
            height={{ xs: "70vh", md: "100vh" }}
            position="relative"
            bgcolor="#F8F8F8"
            sx={{
              borderRadius: "10px",
              border: "1px solid #A9A9A9",
              overflowY: "auto",
            }}
          >
            <Typography
              variant="h5"
              gutterBottom
              fontSize={18}
              p={{ xs: 2, md: 3 }}
              fontWeight={700}
              color="#797979"
            >
              {headers[selectedValue]}
            </Typography>
            <Typography
              sx={{ borderBottom: "1px solid #A9A9A9", width: "100%" }}
            />

            {selectedValue === "contentstructure" ? (
              <ContentStructureField
                contentData={contentData}
                setContentData={setContentData}
                showIcons={showIcons}
                setShowIcons={setShowIcons}
              />
            ) : selectedValue === "sourceofknowledge" ? (
              <SourceOfKnowledgeField
                contentData={contentData}
                setContentData={setContentData}
                showIcons={showIcons}
                setShowIcons={setShowIcons}
              />
            ) : selectedValue === "category" ? (
              <CategoryField
                contentData={contentData}
                setContentData={setContentData}
                showIcons={showIcons}
                setShowIcons={setShowIcons}
              />
            ) : selectedValue === "contentTitle" ? (
              <ContentTitleField
                contentData={contentData}
                setContentData={setContentData}
                showIcons={showIcons}
                setShowIcons={setShowIcons}
              />
            ) : selectedValue === "whattoexpect" ? (
              <WhattoExpectField
                contentData={contentData}
                setContentData={setContentData}
                showIcons={showIcons}
                setShowIcons={setShowIcons}
              />
            ) : selectedValue === "summary" ? (
              <SummaryField
                contentData={contentData}
                setContentData={setContentData}
                showIcons={showIcons}
                setShowIcons={setShowIcons}
              />
            ) : selectedValue === "pricing" ? (
              <PricingField
                contentData={contentData}
                setContentData={setContentData}
                showIcons={showIcons}
                setShowIcons={setShowIcons}
              />
            ) : selectedValue === "promotions" ? (
              <Promotions
                contentData={contentData}
                setContentData={setContentData}
                showIcons={showIcons}
                setShowIcons={setShowIcons}
              />
            ) : selectedValue === "contentthumbnail" ? (
              <ContentThumbnail
                contentData={contentData}
                setContentData={setContentData}
                showIcons={showIcons}
                setShowIcons={setShowIcons}
              />
            ) : selectedValue === "agreeToTerms" ? (
              <AgreeToTerms />
            ) : null}

            <Box
              position={selectedValue === 'contentstructure' ? 'sticky' : 'absolute'
              }
              bottom={0}
              width="100%"
              p={2}
              bgcolor="#F8F8F8"
              display="flex"
              justifyContent={{ xs: "start", md: "center" }}
            >
              {/* Conditionally show the Next or I Agree button */}
              {selectedValue === "agreeToTerms" ? (
                <Button
                  sx={{
                    color: isAgreed ? "white" : "#595959",
                    bgcolor: isAgreed ? "#233F91" : "#DBDBDB",
                    borderRadius: "10px",
                    border: "1px solid #B3B3B3",
                    fontWeight: 400,
                    fontSize: "15px",
                    "&:hover": {
                      bgcolor: isAgreed ? "#233F91" : "#DBDBDB",
                    },
                  }}
                  onClick={handleAgreeClick}
                >
                  I Agree
                </Button>
              ) : (
                <Button
                  sx={{
                    ...styleSheet.nextBtn,
                    color: showIcons[selectedValue] ? "white" : "#595959",
                    bgcolor: showIcons[selectedValue] ? "#233F91" : "#DBDBDB",
                    "&:hover": {
                      bgcolor: showIcons[selectedValue] ? "#233F91" : "#DBDBDB",
                    },
                  }}
                  onClick={handleNextClick}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Grid>
    </>
  );
};

export default CreateHandoutData;

const styleSheet = {
  publicBtn: {
    width: "214px",
    height: "48px",
    p: "6px 20px",
    backgroundColor: "#233F91",
    borderRadius: "15px",
    color: "white",
    "&:hover": {
      backgroundColor: "#233F91",
      color: "white",
    },
  },
  formControl: {
    overflow: "hidden",
    "& .MuiSwitch-switchBase": {
      padding: "3px",
      overflow: "hidden",
      "&.Mui-checked": {
        transform: "translateX(28px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          backgroundColor: "#34C759",
          opacity: 1,
          border: "none",
        },
      },
    },
    "& .MuiSwitch-thumb": {
      width: 28,
      height: 28,
      boxShadow: "none",
    },
    "& .MuiSwitch-track": {
      borderRadius: 26 / 2,
      backgroundColor: "#E9E9EA",
      opacity: 1,
      transition: "background-color 0.3s",
    },
    width: 62,
    height: 34,
    padding: 0,
  },
  switchcss: {
    overflow: "hidden",
    "& .MuiSwitch-switchBase": {
      padding: "3px",
      overflow: "hidden",
      "&.Mui-checked": {
        transform: "translateX(28px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          backgroundColor: "#34C759",
          opacity: 1,
          border: "none",
        },
      },
    },
    "& .MuiSwitch-thumb": {
      width: 28,
      height: 28,
      boxShadow: "none",
    },
    "& .MuiSwitch-track": {
      borderRadius: 26 / 2,
      backgroundColor: "#E9E9EA",
      opacity: 1,
      transition: "background-color 0.3s",
    },
    width: 62,
    height: 34,
    padding: 0,
  },
  nextBtn: {
    borderRadius: "10px",
    border: "1px solid #B3B3B3",
    fontWeight: 400,
    fontSize: "15px",
  },
};
